import { useState, useEffect } from "react";

import ErrorMessage from "../../../components/ErrorMessage/ErrorMessage";
import Pagination from "../../../components/Pagination/Pagination";
import {
  ReportActionButtons,
  SECTION_KEYS,
} from "../../screened-manuscripts/components/ReportActionButtons";
import {
  ApplyAction,
  ColorizeStatusText,
} from "../../../components/ApplyAction";
import {
  convertISOToReadableDate,
  getAmbientScreeningSubmissionById,
  showErrorToast,
} from "../../../helpers";
import {
  handleSetTriggerOrMatchStatus,
  handleSetSubmissionStatus,
} from "../../../helpers/statusHelpers";
import { DataTable } from "stm-frontend-library";
import { EmptyInbox, SignalReportModal } from "../../../components";
import { dropdownOptions } from "../dropdownOptions";
import { getDataTableColumns } from "../columns";
import useDuplicateSubmissions from "../hooks/useDuplicateSubmissions";
import { useAppContext } from "../../../components/AppContext";
import { ModalWrapper } from "../../../components/ModalWrapper/ModalWrapper";

const ArchivedManuscriptsTab = ({ type, isActiveTab }) => {
  const { searchInput } = useAppContext();

  const [selectedItem, setSelectedItem] = useState({
    index: null,
    visibleSection: SECTION_KEYS.ALL,
  });

  const [fetchedData, setFetchedData] = useState(null);
  const [isFetchingAmbientData, setIsFetchingAmbientData] = useState(false);

  const isInboxTabSelected = type === "wo_status";

  const {
    data,
    setData,
    fetchData,
    error,
    pageCount,
    currentPage,
    setCurrentPage,
    searchTerm,
    setSearchTerm,
    isLoading,
  } = useDuplicateSubmissions({ type, isActiveTab });

  const [modal, setModal] = useState({ open: false, type: null });
  const [confirmRequest, setConfirmRequest] = useState(false);
  const [signalCheckRequest, setSignalCheckRequest] = useState(false);
  const [comment, setComment] = useState("");
  const [pendingAction, setPendingAction] = useState(null);

  const updateStatus = async (index, status) => {
    const newData = [...data];
    const previousStatus = newData[index].submission_status;
    newData[index].submission_status = status;

    const { match_id, persistent_id } = newData[index];
    try {
      await handleSetTriggerOrMatchStatus(match_id, persistent_id, status);
    } catch {
      showErrorToast();
      newData[index].submission_status = previousStatus;
    } finally {
      setData(newData);
    }
  };

  const handleIconClick = (index, buttonKey) => {
    setSelectedItem({ index, visibleSection: buttonKey });
  };

  const handleRowClick = (index) => {
    if (index === null) {
      return;
    }
    setSelectedItem({ index, visibleSection: SECTION_KEYS.DUPSUB });
  };

  const handleInterceptedAction = (index, value) => {
    if (value === "duplicate confirmed") {
      setPendingAction({ index, value });
      setModal({ open: true, type: "confirm" });
      setConfirmRequest(false);
      return;
    }
    if (value === "suspect paper mill") {
      setPendingAction({ index, value });
      setModal({ open: true, type: "pm" });
      setSignalCheckRequest(false);
      setComment("");
      return;
    }
    onActionSelect(index, value);
  };

  const handleModalApply = () => {
    if (modal.type === "confirm") {
      alert(
        `Confirm action: Request confirmation from other publisher? ${confirmRequest ? "Yes" : "No"
        }`
      );
      setModal({ open: false, type: null });
      setPendingAction(null);
      return;
    }
    if (modal.type === "pm") {
      alert(
        `Paper Mill Suspect: Add to SignalCheck? ${signalCheckRequest ? "Yes" : "No"}\nComment: ${comment}`
      );
      setModal({ open: false, type: null });
      setPendingAction(null);
      return;
    }
  };

  const handleModalClose = () => {
    setModal({ open: false, type: null });
    setPendingAction(null);
  };

  const onActionSelect = (index, status) => {
    updateStatus(index, status);
  };

  const onActionUndo = (index) => {
    updateStatus(index, null);
  };

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  const dataWithActions = data?.map((element, index) => ({
    ...element,
    raw_match_status: element.match_status,
    timestamp: convertISOToReadableDate(element.timestamp, "DD MMM YYYY"),
    match_status: <ColorizeStatusText statusText={element.match_status} />,
    action: (
      <div className="d-flex">
        <ApplyAction
          index={index}
          status={data[index].submission_status}
          selectAction={handleInterceptedAction}
          undoAction={onActionUndo}
          options={dropdownOptions}
        />
      </div>
    ),
    signals: (
      <ReportActionButtons
        signals={element.signals}
        onIconClick={(buttonKey) => handleIconClick(index, buttonKey)}
      />
    ),
    clickEvent: () => handleRowClick(index),
  }));

  const fetchAmbientScreeningDataIfNeeded = async (index) => {
    const { persistent_id, signals } = data[index];
    if (persistent_id && signals?.some((s) => s !== "duplicateSubmission")) {
      try {
        setIsFetchingAmbientData(true);
        const { data: fetchedData } = await getAmbientScreeningSubmissionById(
          persistent_id
        );
        setFetchedData(fetchedData);
      } finally {
        setIsFetchingAmbientData(false);
      }
    }
  };

  useEffect(() => {
    if (selectedItem.index !== null) {
      fetchAmbientScreeningDataIfNeeded(selectedItem.index);
    } else {
      setFetchedData(null);
    }
  }, [selectedItem, data]);

  useEffect(() => {
    const trimmedValue = searchInput.trim();
    if ((!searchTerm && !trimmedValue) || trimmedValue === searchTerm) return;
    setSearchTerm(trimmedValue);
  }, [searchInput, searchTerm, setSearchTerm]);

  const resetModalState = () => {
    setFetchedData(null);
    setSelectedItem({
      index: null,
      visibleSection: SECTION_KEYS.ALL,
    });
  };

  const hasNonDupSubSignals =
    selectedItem.index !== null &&
    !isFetchingAmbientData &&
    data[selectedItem.index]?.signals?.some(
      (x) => x !== "duplicateSubmissions"
    );

  const shouldRenderModal =
    selectedItem.index !== null &&
    (fetchedData || data[selectedItem.index]) &&
    (hasNonDupSubSignals ? fetchedData : true);

  return (
    <>
      {modal.open && modal.type === "confirm" && (
        <ModalWrapper
          isOpen={true}
          onClose={handleModalClose}
          title="Confirm Manuscript"
          modalClassSize="modal-md"
        >
          <div className="mb-3">
            Do you want to request confirmation from the other publisher?
          </div>
          <div className="mb-3">
            <label>
              <input
                type="checkbox"
                checked={confirmRequest}
                onChange={(e) => setConfirmRequest(e.target.checked)}
              />
              <span className="ms-2">Request confirmation</span>
            </label>
          </div>
          <button className="btn btn-primary" onClick={handleModalApply}>
            Apply
          </button>
        </ModalWrapper>
      )}
      {modal.open && modal.type === "pm" && (
        <ModalWrapper
          isOpen={true}
          onClose={handleModalClose}
          title="Paper Mill Suspect"
          modalClassSize="modal-md"
        >
          <div className="mb-3">
            Do you want to also add this to the SignalCheck?
          </div>
          <div className="mb-3">
            <label>
              <input
                type="checkbox"
                checked={signalCheckRequest}
                onChange={(e) => setSignalCheckRequest(e.target.checked)}
              />
              <span className="ms-2">Add to SignalCheck</span>
            </label>
          </div>
          <div className="mb-3">
            <label>Comment:</label>
            <textarea
              className="form-control"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
            />
          </div>
          <button className="btn btn-primary" onClick={handleModalApply}>
            Apply
          </button>
        </ModalWrapper>
      )}
      {shouldRenderModal && (
        <SignalReportModal
          dupSubData={data[selectedItem.index]}
          contentData={fetchedData}
          visibleSection={selectedItem.visibleSection}
          onClose={resetModalState}
          setTriggerOrMatchStatus={handleSetTriggerOrMatchStatus}
          setSubmissionStatus={handleSetSubmissionStatus}
          fetchData={fetchData}
        />
      )}
      {error && <ErrorMessage />}
      {!error && (
        <>
          <DataTable
            columns={getDataTableColumns()}
            rows={dataWithActions}
            customNoDataComponent={isInboxTabSelected ? EmptyInbox : null}
            isLoading={isLoading}
            loadingMode="overlay"
          />
          <Pagination
            handlePageClick={handlePageClick}
            pageCount={pageCount}
            currentPage={currentPage}
            disabled={isLoading}
          />
        </>
      )}
    </>
  );
};

export default ArchivedManuscriptsTab;
